import axios, { AxiosInstance, AxiosRequestConfig, AxiosPromise } from 'axios';
import { IAuthProvider } from '../providers/DialogAuthProvider';
import { DialogConfig } from '../models/DialogConfig';
import { DialogMessage } from '../models/api/DialogMessage';

const DialogApiRoutes = {
    authorize: '/api/access/authorize',
    entities: '/api/entities',
    categories: '/api/categories',
    audiences: '/api/audiences',
    tabs: '/api/tabs',
    messages: '/api/messages',
    createMessage: '/api/messages/create'
}

export class DialogHttpService
{
    private _axios: AxiosInstance;
    private _config: DialogConfig
    private _authProvider: IAuthProvider;
    private _expires: Date;

    public constructor(config: DialogConfig, authProvider: IAuthProvider) {
        this._config = config;
        this._authProvider = authProvider;
        this._axios = axios.create({
            baseURL: this._config.ServiceUrl
        });
    }
    public getEntities(type: string): Promise<any> {
        let config: AxiosRequestConfig = {
            url: `${this._config.ServiceUrl}${DialogApiRoutes.entities}/all/${type}`,
            method: 'get'
        }
        return this.request(config);
    }
    public getAudiences(): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.audiences,
            method: 'get'
        }
        return this.request(config);
    }
    public getAudience(id: string): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.audiences + '/' + id,
            method: 'get'
        }
        return this.request(config);
    }
    public createAudience(audience: any): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.audiences,
            data: audience,
            method: 'post'
        }
        return this.request(config);
    }
    public updateAudience(audience: any): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.categories + '/' + audience.id,
            data: audience,
            method: 'put'
        }
        return this.request(config);
    }
    public deleteAudience(id: string): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.audiences + '/' + id,
            method: 'delete'
        }
        return this.request(config);
    }
    public getCategories(): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.categories,
            method: 'get'
        }
        return this.request(config);
    }
    public getCategory(id: string): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.categories + '/' + id,
            method: 'get'
        }
        return this.request(config);
    }
    public createCategory(category: any): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.categories,
            data: category,
            method: 'post'
        }
        return this.request(config);
    }
    public updateCategory(category: any): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.categories + '/' + category.id,
            data: category,
            method: 'put'
        }
        return this.request(config);
    }
    public deleteCategory(id: string): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.categories + '/' + id,
            method: 'delete'
        }
        return this.request(config);
    }
    public getTabs(): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.tabs,
            method: 'get'
        }
        return this.request(config);
    }
    public getTab(id: string): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.tabs + '/' + id,
            method: 'get'
        }
        return this.request(config);
    }
    public createTab(tab: any): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.tabs,
            data: tab,
            method: 'post'
        }
        return this.request(config);
    }
    public updateTab(tab: any): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.tabs + '/' + tab.id,
            data: tab,
            method: 'put'
        }
        return this.request(config);
    }
    public deleteTab(id: string): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.tabs + '/' + id,
            method: 'delete'
        }
        return this.request(config);
    }
    public addMessage(contents: DialogMessage): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.createMessage,
            method: 'post',
            data: contents
        }
        return this.request(config);
    }
    public getMessage(id: number): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.messages + '/' + id,
            method: 'get'
        }
        return this.request(config);
    }
    public updateMessage(contents: DialogMessage): Promise<any> {
        let config: AxiosRequestConfig = {
            url: this._config.ServiceUrl + DialogApiRoutes.tabs + '/' + contents.id,
            data: contents,
            method: 'put'
        }
        return this.request(config);
    }
    public request(config: AxiosRequestConfig): Promise<any> {
        if(this._expires && this._expires.valueOf() > Date.now()) {
            return this._axios.request(config);
        }
        return this.authorize().then(a => {
            return this._axios.request(config);
        });
    }
    public authorize(): AxiosPromise<any> {
        return this._axios
            .request(this._authProvider.authorizeRequestInterceptor({
                headers: {},
                baseUrl: this._config.ServiceUrl,
                url: DialogApiRoutes.authorize,
                method: 'post'
            }))
            .then(r => this._authProvider.authorizeResponseInterceptor(r))
            .then(r => {
                this._axios.interceptors.request.use(this._authProvider.requestInterceptor.bind(this._authProvider));
                this._expires = r.data.expires ?
                    new Date(r.data.expires) :
                    new Date();
                return r;
            });
    }
}